import { useCallback, useEffect, useRef, useState } from 'react';
import { acceptedSceneShrinkNeedsConfirm } from '../utils/acceptedSceneShrink';
import { DRAFT_LEAVE_WARNING } from '../utils/writerCopy';
import {
  removeUnsavedDraft,
  removeUnsavedDraftIfSaved,
  writeUnsavedDraft,
} from '../utils/sceneDraftLocal';
import { useUnsavedChangesWarning } from './useUnsavedChangesWarning';

export const DRAFT_AUTOSAVE_MS = 1500;

const samePass = (a, b) => String(a || '') === String(b || '');

/**
 * Debounced save of the open draft. Blur and flush save immediately.
 * A pending or failed save warns on reload and on in-app links.
 * An accepted scene passes active only while she is editing it.
 * A save that would replace most of that accepted scene waits
 * until it is confirmed. Keep editing leaves the typed words in the box.
 */
export function useSceneDraftAutosave({
  active,
  storyId,
  sceneId,
  passId,
  text,
  serverText,
  saveDraft,
  onOpenScene,
  guardAcceptedShrink = false,
}) {
  const [status, setStatus] = useState('idle');
  const [shrinkAsk, setShrinkAsk] = useState(null);
  const saved = useRef({ passId: '', text: '' });
  const timer = useRef(0);
  const inflight = useRef(null);
  const declined = useRef('');
  const confirmed = useRef('');
  const latest = useRef({});
  latest.current = {
    active,
    storyId,
    sceneId,
    passId,
    text,
    saveDraft,
    onOpenScene,
    guardAcceptedShrink,
  };

  const classify = useCallback((snap) => {
    const guard = Boolean(snap?.guardAcceptedShrink);
    const wipe = guard && acceptedSceneShrinkNeedsConfirm(saved.current.text ?? '', snap?.text ?? '');
    const allowed = Boolean(snap?.confirmShrink) || snap?.text === confirmed.current;
    if (!wipe || allowed) return 'save';
    if (snap?.text === declined.current) return 'declined';
    return 'ask';
  }, []);

  const serverKey = `${passId || ''}\0${serverText ?? ''}`;
  const seenServer = useRef(null);
  if (seenServer.current !== serverKey) {
    seenServer.current = serverKey;
    saved.current = { passId: String(passId || ''), text: serverText ?? '' };
  }

  const dirty = Boolean(
    active
    && storyId
    && sceneId
    && passId
    && samePass(passId, saved.current.passId)
    && text !== saved.current.text,
  );

  const runSave = useCallback(async (snap) => {
    if (!snap?.active || !snap.passId || !snap.storyId || !snap.sceneId || !snap.saveDraft) {
      return true;
    }
    if (snap.text === saved.current.text && samePass(snap.passId, saved.current.passId)) {
      return true;
    }
    const kind = classify(snap);
    if (kind === 'ask') {
      setShrinkAsk(snap.text);
      setStatus('idle');
      return 'held';
    }
    if (kind === 'declined') {
      setStatus('idle');
      return false;
    }
    const key = `${snap.passId}\0${snap.text}`;
    if (inflight.current?.key === key) return inflight.current.promise;
    let promise;
    promise = (async () => {
      try {
        const body = {
          sceneId: snap.sceneId,
          passId: snap.passId,
          generatedText: snap.text,
        };
        if (snap.confirmShrink || snap.text === confirmed.current) body.confirmShrink = true;
        if (snap.keepalive) await snap.saveDraft(snap.storyId, body, { keepalive: true });
        else await snap.saveDraft(snap.storyId, body);
        removeUnsavedDraftIfSaved(snap.storyId, snap.sceneId, snap.passId, snap.text);
        const now = latest.current;
        if (!samePass(now.passId, snap.passId)) return true;
        saved.current = { passId: String(snap.passId), text: snap.text };
        if (now.text === snap.text) {
          setStatus('saved');
        } else {
          // Typed on during the request: the copy now sits on the saved text.
          writeUnsavedDraft(now.storyId, now.sceneId, now.passId, snap.text, now.text);
          setStatus('saving');
        }
        return true;
      } catch {
        const now = latest.current;
        if (now.active && samePass(now.passId, snap.passId) && now.text === snap.text) {
          setStatus('error');
        }
        return false;
      } finally {
        if (inflight.current?.promise === promise) inflight.current = null;
      }
    })();
    inflight.current = { key, promise };
    return promise;
  }, [classify]);

  const flush = useCallback(async (opts) => {
    const showHold = opts?.showHold !== false;
    const reask = opts?.reask === true;
    window.clearTimeout(timer.current);
    timer.current = 0;
    const snap = { ...latest.current, keepalive: opts?.keepalive === true };
    if (!snap.active || !snap.passId || !snap.storyId || !snap.sceneId) return true;
    if (snap.text === saved.current.text && samePass(snap.passId, saved.current.passId)) {
      declined.current = '';
      return true;
    }
    let kind = classify(snap);
    if (reask && kind === 'declined') {
      declined.current = '';
      kind = 'ask';
    }
    if (kind === 'ask') {
      if (showHold) setShrinkAsk(snap.text);
      setStatus('idle');
      return showHold ? 'held' : false;
    }
    if (kind === 'declined') {
      setStatus('idle');
      return 'declined';
    }
    setStatus('saving');
    return runSave(snap);
  }, [classify, runSave]);

  const confirmShrink = useCallback(async () => {
    const snap = { ...latest.current, confirmShrink: true };
    confirmed.current = snap.text;
    declined.current = '';
    setShrinkAsk(null);
    window.clearTimeout(timer.current);
    timer.current = 0;
    setStatus('saving');
    return runSave(snap);
  }, [runSave]);

  const dismissShrink = useCallback(() => {
    declined.current = latest.current.text ?? '';
    setShrinkAsk(null);
    setStatus('idle');
  }, []);

  useEffect(() => {
    setShrinkAsk(null);
    declined.current = '';
    confirmed.current = '';
  }, [passId, sceneId, storyId]);

  useEffect(() => {
    if (!dirty) return undefined;
    const kind = classify({ ...latest.current });
    if (kind === 'declined') {
      setStatus('idle');
      return undefined;
    }
    if (kind === 'save') {
      setStatus((prev) => (prev === 'saving' ? prev : 'saving'));
    } else {
      setStatus('idle');
    }
    timer.current = window.setTimeout(() => {
      void runSave({ ...latest.current });
    }, DRAFT_AUTOSAVE_MS);
    return () => window.clearTimeout(timer.current);
  }, [dirty, text, passId, sceneId, storyId, active, runSave, classify]);

  useEffect(() => {
    if (dirty) return undefined;
    declined.current = '';
    if (!inflight.current && status === 'saving') setStatus('idle');
    if (status === 'error' && text === saved.current.text) setStatus('idle');
    return undefined;
  }, [dirty, status, text]);

  const blocked = Boolean(active && (dirty || status === 'saving' || status === 'error'));
  const blockedRef = useRef(false);
  blockedRef.current = blocked;
  useUnsavedChangesWarning(blocked, DRAFT_LEAVE_WARNING);

  useEffect(() => {
    const onClick = (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const anchor = event.target?.closest?.('a[href]');
      if (!anchor || anchor.target === '_blank') return;
      let url;
      try {
        url = new URL(anchor.getAttribute('href'), window.location.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) return;
      const story = String(latest.current.storyId || '');
      const match = url.pathname.match(/^\/scene-writing\/([^/]+)\/?$/);
      if (match && story && match[1] === story) {
        const nextScene = url.searchParams.get('scene') || '';
        const here = String(latest.current.sceneId || '');
        if (nextScene && nextScene !== here && latest.current.onOpenScene) {
          event.preventDefault();
          event.stopPropagation();
          latest.current.onOpenScene(nextScene);
        }
        return;
      }
      if (!blockedRef.current) return;
      if (!window.confirm(DRAFT_LEAVE_WARNING)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  // Keep a browser copy of text the server does not have yet.
  useEffect(() => {
    if (!dirty) return;
    writeUnsavedDraft(storyId, sceneId, passId, saved.current.text, text);
  }, [dirty, text, storyId, sceneId, passId]);

  // A different pass id means the old pass's copy is no longer wanted.
  const lastPassKey = useRef(null);
  useEffect(() => {
    const prev = lastPassKey.current;
    lastPassKey.current = { storyId, sceneId, passId };
    if (prev && prev.passId && !samePass(prev.passId, passId)) {
      removeUnsavedDraft(prev.storyId, prev.sceneId, prev.passId);
    }
  }, [storyId, sceneId, passId]);

  useEffect(() => {
    const onHide = () => {
      const now = latest.current;
      if (
        now.active
        && now.passId
        && samePass(now.passId, saved.current.passId)
        && now.text !== saved.current.text
      ) {
        writeUnsavedDraft(now.storyId, now.sceneId, now.passId, saved.current.text, now.text);
      }
      void flush({ showHold: false, keepalive: true });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [flush]);

  return { status, blocked, dirty, flush, shrinkAsk, confirmShrink, dismissShrink };
}
